
import { Type } from "@earendil-works/pi-ai";

import { SPEC_DOC } from "./kernelPrimitiveSpecs.js";
import { coerceJsonArg } from "./coerceJsonArg.js";

export function createRenderBlockTool() {
    return {
        name: "render_block",
        label: "Render block",
        description:
            "Surface a result in the chat using one of the dashboard's stable render primitives — single-value tiles (stat / kpis / donut / sparkline), categorical lists (bars / rows / table), composition (treemap / sunburst), distribution (boxplot / scatter / radar / parallel), time/series (chart), density (heatmap / calendar / timeline), and flow (funnel / sankey / chord). Prefer this over pasting a JSON dump when the data is naturally visual or tabular.\n\n" +
            "Use this to present the final result of an analysis. Keep the spec JSON-serialisable; no functions, no JSX.\n\n" +
            "IMPORTANT — `data` must be the ACTUAL inline JSON values (numbers, arrays, objects), not a reference name. There is no step-ref resolution in this tool. Strings like \"result\" or \"output\" are NOT resolved; they'll render as an empty block. If you computed values in a previous step (analytics_query, execute_python), inline the actual numbers/rows here. This tool renders a snapshot of the values you pass.\n\n" +
            SPEC_DOC,
        parameters: Type.Object({
            spec: Type.Object({
                kind: Type.String({
                    description:
                        "The render primitive to use (e.g. \"chart\", \"bars\", \"stat\", \"treemap\"). See the spec doc below for the full set and each kind's fields.",
                }),
            }, {
                additionalProperties: true,
                description:
                    "Render spec — a JSON object (NOT a JSON-encoded string). Must include `kind` plus the per-kind fields. See description for valid shapes.",
            }),
            data: Type.Unknown({
                description:
                    "Computed values for this render — INLINE JSON only (numbers, arrays, objects). Shape depends on the kind (array of numbers for sparkline, array of rows for bars/table/rows, scalar or object for stat). Do not pass a string like \"result\" expecting it to resolve to a previous step's output; this tool has no ref resolution.",
            }),
        }),
        async execute(_id, args) {
            // Some model families double-encode object/array params as a
            // JSON string. Decode before use so a stringified spec doesn't
            // render as "invalid spec" on one model but work on another.
            const spec = coerceJsonArg(args.spec);
            const data = coerceJsonArg(args.data);
            const summary = describeBlock(spec, data);
            return {
                content: [{ type: "text", text: summary }],
                details: { kind: "kernelBlock", spec, data },
            };
        },
    };
}

export const renderBlockTool = createRenderBlockTool({ getNamespaceKey: () => "default" });

function describeBlock(spec, data) {
    if (!spec || typeof spec !== "object") return "rendered (invalid spec)";
    const kind = spec.kind || "unknown";
    if (Array.isArray(data)) {
        return `rendered ${kind} block · ${data.length} row${data.length === 1 ? "" : "s"}`;
    }
    return `rendered ${kind} block`;
}
